import { useState } from "react";
import useContactFormValidation from "../../hooks/useContactFormValidation";

const formFields = [
    { id: "name", label: "Name", type: "text" },
    { id: "email", label: "Email", type: "email" },
    { id: "message", label: "Message", type: "textarea" },
];

const ContactForm = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        message: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [responseMessage, setResponseMessage] = useState("");
    const [isError, setIsError] = useState(false);

    const requiredFields = ["name", "email", "message"];
    const { errors, touched, isFormValid, markFieldTouched } = useContactFormValidation(
        formData,
        requiredFields
    );

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleBlur = (e) => {
        markFieldTouched(e.target.name);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const response = await fetch("/api/contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });

            const data = await response.json().catch(() => ({}));

            if (response.ok) {
                setIsError(false);
                setResponseMessage("Message sent successfully!");
                setTimeout(() => {
                    setResponseMessage("");
                    setFormData({ name: "", email: "", message: "" });
                }, 5000);
            } else {
                setIsError(true);
                setResponseMessage(
                    data.message || "Failed to send message. Please try again later."
                );
                setTimeout(() => setResponseMessage(""), 5000);
            }
        } catch (error) {
            setIsError(true);
            setResponseMessage("Failed to send message. Please try again later.");
            setTimeout(() => setResponseMessage(""), 5000);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
          <section className="relative py-24 px-4 scroll-mt-24 bg-[#f7f8f7]">
      <div className="relative z-10 max-w-2xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-[#3a3a3a] text-center mb-4">
          Get in Touch
        </h2>
        <p className="text-[#6b7280] text-lg text-center mb-10 leading-relaxed">
          Have a suggestion, or just want to say hello? Fill out the form below, and we'll get back to you!
        </p>

        <form
          onSubmit={handleSubmit}
          className="bg-white border border-gray-100 rounded-3xl p-8 md:p-10 flex flex-col gap-6 shadow-sm"
        >
          {formFields.map((field) => (
            <div key={field.id} className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <label
                  htmlFor={field.id}
                  className="text-[#3a3a3a] font-semibold text-sm"
                >
                  {field.label} <span className="text-red-500">*</span>
                </label>
                {touched[field.id] && errors[field.id] && (
                  <span className="text-red-500 text-xs">
                    {errors[field.id]}
                  </span>
                )}
              </div>
              {field.type === "textarea" ? (
                <textarea
                  id={field.id}
                  name={field.id}
                  value={formData[field.id]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={`Enter your ${field.label.toLowerCase()}`}
                  rows={5}
                  className={`bg-[#f7f8f7] border rounded-xl py-3 px-4 text-[#3a3a3a] placeholder-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#6B8F8A]/30 focus:border-[#6B8F8A]/30 transition-all resize-none ${
                    touched[field.id] && errors[field.id]
                      ? "border-red-300"
                      : "border-gray-200"
                  }`}
                  aria-invalid={touched[field.id] && !!errors[field.id]}
                />
              ) : (
                <input
                  type={field.type}
                  id={field.id}
                  name={field.id}
                  value={formData[field.id]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={`Enter your ${field.label.toLowerCase()}`}
                  className={`bg-[#f7f8f7] border rounded-xl py-3 px-4 text-[#3a3a3a] placeholder-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#6B8F8A]/30 focus:border-[#6B8F8A]/30 transition-all ${
                    touched[field.id] && errors[field.id]
                      ? "border-red-300"
                      : "border-gray-200"
                  }`}
                  aria-invalid={touched[field.id] && !!errors[field.id]}
                />
              )}
            </div>
          ))}

          {responseMessage && (
            <p
              className={`text-sm font-medium text-center ${
                isError ? "text-red-500" : "text-[#6B8F8A]"
              }`}
            >
              {responseMessage}
            </p>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !isFormValid}
            className="bg-[#6B8F8A] hover:bg-[#5a7f7a] disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-3.5 px-8 rounded-full transition-colors duration-200 shadow-md hover:shadow-lg"
          >
            {isSubmitting ? "Sending..." : "Send Message"}
          </button>
        </form>
      </div>
    </section>
  );
};

export default ContactForm;
